import { useState } from "react";
import { useTranslation } from "react-i18next";
import { ShoppingBasket, Globe, Menu, X } from "lucide-react";
import { useCart } from "../context/CartContext";

const LANGS = [
  { code: "id", label: "ID" }, { code: "en", label: "EN" }, { code: "ar", label: "AR" },
  { code: "fr", label: "FR" }, { code: "es", label: "ES" }, { code: "zh", label: "中文" },
  { code: "ko", label: "한국어" }, { code: "ja", label: "日本語" },
];

export const Navbar = () => {
  const { t, i18n } = useTranslation();
  const { totals, setCartOpen, currency, setCurrency, lang } = useCart();
  const [langOpen, setLangOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  const links = [
    { href: "#videos", label: t("nav.videos") },
    { href: "#products", label: t("nav.products") },
    { href: "#dosage", label: t("nav.dosage") },
    { href: "#faq", label: t("nav.faq") },
  ];

  return (
    <header className="fixed top-0 inset-x-0 z-40 backdrop-blur-xl bg-white/80 border-b border-brand-green/10">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 flex items-center justify-between h-16">
        <a href="#top" data-testid="nav-logo" className="font-heading text-3xl text-brand-dark tracking-tight">
          AGRO<span className="text-brand-orange">DYKE</span>
        </a>
        <nav className="hidden md:flex items-center gap-8">
          {links.map((l) => (
            <a key={l.href} href={l.href} data-testid={`nav-link-${l.href.slice(1)}`}
              className="text-sm font-semibold text-brand-brown hover:text-brand-dark transition-colors">{l.label}</a>
          ))}
        </nav>
        <div className="flex items-center gap-2 sm:gap-3">
          <button data-testid="currency-switcher"
            onClick={() => setCurrency(currency === "IDR" ? "USD" : "IDR")}
            className="px-3 py-1.5 rounded-full text-xs font-bold bg-brand-cream text-brand-dark border border-brand-green/20 hover:bg-brand-yellow transition-colors">
            {currency}
          </button>
          <div className="relative">
            <button data-testid="language-switcher" onClick={() => setLangOpen(!langOpen)}
              className="flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-bold bg-brand-cream text-brand-dark border border-brand-green/20 hover:bg-brand-yellow transition-colors">
              <Globe size={14} /> {lang.toUpperCase()}
            </button>
            {langOpen && (
              <div className="absolute end-0 mt-2 w-28 max-h-72 overflow-y-auto bg-white rounded-2xl shadow-lg border border-brand-green/10 overflow-hidden py-1 z-50">
                {LANGS.map((l) => (
                  <button key={l.code} data-testid={`lang-option-${l.code}`}
                    onClick={() => { i18n.changeLanguage(l.code); setLangOpen(false); }}
                    className={`block w-full text-start px-4 py-2 text-sm font-semibold hover:bg-brand-cream transition-colors ${lang === l.code ? "text-brand-orange" : "text-brand-brown"}`}>
                    {l.label}
                  </button>
                ))}
              </div>
            )}
          </div>
          <button data-testid="cart-button" onClick={() => setCartOpen(true)}
            className="relative p-2 rounded-full bg-brand-orange text-white hover:bg-brand-dark transition-colors">
            <ShoppingBasket size={18} />
            {totals.count > 0 && (
              <span data-testid="cart-count" className="absolute -top-1 -end-1 w-5 h-5 rounded-full bg-brand-dark text-white text-[10px] font-bold flex items-center justify-center">
                {totals.count}
              </span>
            )}
          </button>
          <button data-testid="mobile-menu-button" className="md:hidden p-2 text-brand-dark" onClick={() => setMobileOpen(!mobileOpen)}>
            {mobileOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </div>
      {mobileOpen && (
        <nav className="md:hidden bg-white border-t border-brand-green/10 px-6 py-4 space-y-3">
          {links.map((l) => (
            <a key={l.href} href={l.href} onClick={() => setMobileOpen(false)}
              className="block text-sm font-semibold text-brand-brown">{l.label}</a>
          ))}
        </nav>
      )}
    </header>
  );
};
